/**
 * Live plumbing for the Command Center's Recent DevOps Activity feed — a THIN
 * SELECTOR over the shared realtime bus (`src/realtime/`).
 *
 * THE DUPLICATE PARSER THIS FILE USED TO BE:
 * `devops-event` was parsed twice. `views/devops/devopsLiveStatus.ts` flattened
 * the envelope for the pipeline/work-item grids and deliberately dropped
 * `clientId` and the headline-bearing summary fields; this module therefore
 * opened its OWN socket and kept the raw envelope, plus a copy of the API's
 * `buildDevOpsEventTitle`. Now the canonical
 * {@link import("realtime").DevOpsRealtimeEvent} carries the envelope, the
 * title and the flat projection together, parsed once, and each consumer picks
 * a field. The dashboard picks `envelope`.
 *
 * CONNECTION: none is opened here — see `realtime/realtimeBus.ts`.
 * AUTH: sourced internally by the bus from the AuthStore; there is no token
 * parameter, which is what makes the old "subscribed with no token, received
 * nothing, logged nothing" state unrepresentable. (The Command Center was one
 * of the callers that hit it.)
 *
 * IF THE STREAM IS DOWN: the bus records a loud diagnostic and the dashboard
 * falls back to its 60s poll — see `realtime/freshnessPolicy.ts`.
 */
import type RootStore from "stores/RootStore";
import {
  DEVOPS_EVENT_WIRE,
  buildDevOpsEventTitle,
  devopsChannel,
  subscribeRealtime,
  type DevOpsEnvelope,
} from "realtime";

/** Wire event name: `"<servicePath> <customEvent>"` (see module doc). */
export const HOME_DEVOPS_EVENT = DEVOPS_EVENT_WIRE;

/**
 * The raw envelope the API publishes: `{ type, connectionId, clientId,
 * summary, at }`. Re-exported from the canonical channel — one definition.
 */
export type HomeDevopsEnvelope = DevOpsEnvelope;

/**
 * Headline builder, a faithful port of the API's `buildDevOpsEventTitle`.
 * Re-exported from the canonical translator so the live row and the persisted
 * `devops_events.title` column cannot drift apart.
 */
export { buildDevOpsEventTitle };

type HomeDevopsHandler = (envelope: HomeDevopsEnvelope) => void;

/**
 * Subscribe to live devops-event envelopes. Returns an idempotent disposer.
 *
 * Unlike the DevOps grids, this receives EVERY object payload, including
 * envelope types that map to no grid entity — the feed shows them by title.
 *
 * @param rootStore used by the bus to resolve the shared connection and the
 *   AuthStore JWT. The caller cannot supply a token.
 */
export function subscribeHomeDevopsEvents(
  rootStore: RootStore,
  handler: HomeDevopsHandler
): () => void {
  return subscribeRealtime(rootStore, devopsChannel, (event) => {
    handler(event.envelope);
  });
}
